import React, { useState } from "react";
import "./DesignProjectCard.css";
import { Fade } from "react-reveal";

export default function DesignProjectCard({ repo, theme, openModal }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Add safety checks for repo and repo.images
  if (!repo || !repo.images || !Array.isArray(repo.images) || repo.images.length === 0) {
    return (
      <div className="design-card-div" style={{ backgroundColor: theme.highlight }}>
        <Fade bottom duration={2000} distance="40px">
          <div className="design-content">
            <div className="design-header">
              <div className="design-name-div">
                <h3 className="design-name" style={{ color: theme.text }}>
                  {repo?.name || "Project Name"}
                </h3>
                <div className="design-client" style={{ color: theme.secondaryText }}>
                  {repo?.client || "Client"}
                </div>
              </div>
            </div>
            <p className="design-description" style={{ color: theme.text }}>
              {repo?.description || "No description available"}
            </p>
            <div className="design-meta">
              <p style={{ color: theme.secondaryText }}>No images available for this project</p>
            </div>
          </div>
        </Fade>
      </div>
    );
  }

  function openProjectInNewTab(url) {
    if (url) {
      var win = window.open(url, "_blank");
      win.focus();
    }
  }

  const handleImageNavigation = (direction, e) => {
    e.stopPropagation();
    const totalImages = repo.images.length;
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  const handleImageClick = (e) => {
    e.stopPropagation(); // Empêche l'ouverture du lien
    const imageSrc = require(`../../assets/images/${repo.images[currentImageIndex]}`);
    openModal(imageSrc);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      "UI/UX": "🎨",
      "Social Media": "📱",
      "Branding": "💼",
      "Web Design": "💻",
      "Mobile Design": "📱",
      "Graphic Design": "🖼️"
    };
    return icons[category] || "🎯";
  };

  const getCategoryColor = (category) => {
    const colors = {
      "UI/UX": "#6366f1",
      "Social Media": "#059669",
      "Branding": "#f59e0b",
      "Web Design": "#10b981",
      "Mobile Design": "#8b5cf6",
      "Graphic Design": "#ef4444"
    };
    return colors[category] || "#6b7280";
  };

  return (
    <div className="design-card-div" style={{ backgroundColor: theme.highlight }}>
      <Fade bottom duration={2000} distance="40px">
        <div key={repo.id}>
          {/* Project Image Gallery Section */}
          <div 
            className="design-image-container"
            onClick={handleImageClick}
          >
            <div className="design-image-wrapper">
              <img
                src={require(`../../assets/images/${repo.images[currentImageIndex]}`)}
                alt={`${repo.name || 'Project'} - ${currentImageIndex + 1}`}
                className="design-image"
              />

              {/* Image Navigation */}
              {repo.images.length > 1 && (
                <>
                  <button
                    className="image-nav-btn prev-btn"
                    onClick={(e) => handleImageNavigation('prev', e)}
                    aria-label="Previous"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15,18 9,12 15,6"></polyline>
                    </svg>
                  </button>
                  <button
                    className="image-nav-btn next-btn"
                    onClick={(e) => handleImageNavigation('next', e)}
                    aria-label="Next"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9,18 15,12 9,6"></polyline>
                    </svg>
                  </button>
                </>
              )}

              {/* Image Indicators */}
              {repo.images.length > 1 && (
                <div className="image-indicators">
                  {repo.images.map((_, index) => (
                    <div
                      key={index}
                      className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Category Badge */}
            <div
              className="category-badge"
              style={{ backgroundColor: getCategoryColor(repo.category) }}
            >
              <span className="category-icon">{getCategoryIcon(repo.category)}</span>
              <span className="category-text">{repo.category || 'General'}</span>
            </div>
          </div>

          <div 
            className="design-content"
            onClick={() => openProjectInNewTab(repo.url)}
          >
            <div className="design-header">
              <div className="design-name-div">
                <h3 className="design-name" style={{ color: theme.text }}>
                  {repo.name}
                </h3>
                <div className="design-client" style={{ color: theme.secondaryText }}>
                  {repo.client}
                </div>
              </div>
            </div>

            <p className="design-description" style={{ color: theme.text }}>
              {repo.description}
            </p>

            <div className="design-details">
              <div className="design-tools">
                {repo.tools && Array.isArray(repo.tools) && repo.tools.map((tool, index) => (
                  <span
                    key={index}
                    className="tool-tag"
                    style={{
                      backgroundColor: `${getCategoryColor(repo.category)}15`,
                      color: getCategoryColor(repo.category)
                    }}
                  >
                    {tool.iconifyClass && <i className={tool.iconifyClass}></i>}
                    {tool.name}
                  </span>
                ))}
              </div>

              <div className="design-meta">
                {repo.completedAt && (
                  <p className="design-date" style={{ color: theme.secondaryText }}>
                    {repo.completedAt}
                  </p>
                )}
                {repo.duration && (
                  <p className="design-duration" style={{ color: theme.secondaryText }}>
                    Duration: {repo.duration}
                  </p>
                )}
              </div>
            </div>

            {/* Project Links */}
            {(repo.liveUrl || repo.behanceUrl || repo.dribbbleUrl) && (
              <div className="design-links">
                {repo.liveUrl && (
                  <a
                    href={repo.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="design-link live-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15,3 21,3 21,9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                    Live View
                  </a>
                )}
                {repo.behanceUrl && (
                  <a
                    href={repo.behanceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="design-link behance-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="logos-behance"></i>
                    Behance
                  </a>
                )}
                {repo.dribbbleUrl && (
                  <a
                    href={repo.dribbbleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="design-link dribbble-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="logos-dribbble"></i>
                    Dribbble
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </Fade>
    </div>
  );
}